<script setup lang="ts">
  import { computed, ref, type Component } from "vue";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { Check, PenLine, Copy, RotateCcw, Trash2, TriangleAlert } from "lucide-vue-next";

  const props = withDefaults(
    defineProps<{
      index: number;
      elementTitle: string;
      elementSubtitle?: string | null;
      icon?: Component | null;
      selectElement?: (index: number) => void;
      deleteElement?: ((index: number) => void) | null;
      confirmDeletion?: boolean;
      duplicateElement?: ((index: number) => void) | null;
      isElementSelected?: (index: number) => boolean;
      elementErrorMessages?: (index: number) => string[];
      editElementTitle?: ((index: number, value: string) => void) | null;
      saveTitleChanges?: ((index: number) => void | Promise<void>) | null;
      restoreTitleChanges?: ((index: number) => void) | null;
      visible?: boolean;
    }>(),
    {
      elementSubtitle: null,
      icon: null,
      selectElement: () => {},
      deleteElement: null,
      confirmDeletion: false,
      duplicateElement: null,
      isElementSelected: () => false,
      elementErrorMessages: () => [],
      editElementTitle: null,
      saveTitleChanges: null,
      restoreTitleChanges: null,
      visible: true,
    },
  );

  const isEditingTitle = ref(false);

  const errors = computed(() => props.elementErrorMessages(props.index) ?? []);
  const hasErrors = computed(() => errors.value.length > 0);
  const selected = computed(() => props.isElementSelected(props.index));
  const hasBuiltInActions = computed(() => !!(props.editElementTitle || props.duplicateElement || props.deleteElement));

  const handleTitleEdition = () => (isEditingTitle.value = !isEditingTitle.value);
  const handleSaveTitleChanges = async () => {
    isEditingTitle.value = false;
    if (props.saveTitleChanges) await props.saveTitleChanges(props.index);
  };
  const handleRestoreTitleChanges = () => {
    isEditingTitle.value = false;
    if (props.restoreTitleChanges) props.restoreTitleChanges(props.index);
  };
  const handleDelete = () => {
    if (props.deleteElement) props.deleteElement(props.index);
  };
</script>

<template>
  <div
    v-if="visible"
    :id="`list-item-${index}`"
    class="group/list-item flex cursor-pointer flex-col rounded-md transition-colors hover:bg-muted"
    :class="{
      'shadow-[inset_0_0_0_1px_var(--ring)]': selected,
      'shadow-[inset_0_0_0_1px_var(--destructive)]': hasErrors,
    }">
    <div class="flex min-h-8 w-full items-center gap-1.5 px-1.5 py-1">
      <div
        v-if="$slots.prefix"
        class="flex items-center"
        @click.stop>
        <slot name="prefix" />
      </div>
      <div
        v-if="$slots.media"
        class="flex shrink-0 items-center"
        @click="selectElement(index)">
        <slot name="media" />
      </div>
      <span
        v-if="icon"
        class="flex size-6 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground"
        @click="selectElement(index)">
        <component
          :is="icon"
          class="size-3.5"
          :stroke-width="1.8" />
      </span>
      <div
        class="flex min-w-0 flex-1 items-center gap-2 self-stretch"
        @click="selectElement(index)">
        <Input
          v-if="isEditingTitle && editElementTitle"
          class="h-7 flex-1 text-13"
          :model-value="elementTitle"
          @click.stop
          @update:model-value="editElementTitle(index, String($event))" />
        <div
          v-else
          class="flex min-w-0 flex-1 flex-col py-0.5">
          <span class="flex items-center gap-1.5 truncate text-13 font-medium text-primary">
            <Tooltip
              v-if="hasErrors"
              :content="errors.join(', ')">
              <TriangleAlert
                class="size-3.5 shrink-0 text-status-error"
                :stroke-width="1.8" />
            </Tooltip>
            <span class="truncate">{{ elementTitle || "Untitled" }}</span>
          </span>
          <span
            v-if="elementSubtitle"
            class="truncate text-xs text-foreground-subtle">
            {{ elementSubtitle }}
          </span>
        </div>
        <slot name="details" />
      </div>
      <div
        v-if="hasBuiltInActions || $slots.actions"
        class="flex items-center gap-0.5">
        <div
          v-if="hasBuiltInActions"
          class="flex items-center gap-0.5 transition-all duration-200"
          :class="
            selected || isEditingTitle
              ? 'w-auto'
              : 'w-0 overflow-hidden group-hover/list-item:w-auto group-hover/list-item:overflow-visible focus-within:w-auto focus-within:overflow-visible'
          ">
          <Button
            v-if="editElementTitle && isEditingTitle"
            variant="ghost"
            size="icon-sm"
            class="size-6"
            aria-label="Save title"
            @click.stop="handleSaveTitleChanges">
            <Check class="size-3.5" />
          </Button>
          <Button
            v-else-if="editElementTitle && !isEditingTitle"
            variant="ghost"
            size="icon-sm"
            class="size-6"
            aria-label="Rename"
            @click.stop="handleTitleEdition">
            <PenLine class="size-3.5" />
          </Button>
          <Button
            v-if="duplicateElement"
            variant="ghost"
            size="icon-sm"
            class="size-6"
            aria-label="Duplicate"
            @click.stop="duplicateElement(index)">
            <Copy class="size-3.5" />
          </Button>
          <Button
            v-if="editElementTitle && isEditingTitle"
            variant="ghost"
            size="icon-sm"
            class="size-6"
            aria-label="Cancel"
            @click.stop="handleRestoreTitleChanges">
            <RotateCcw class="size-3.5" />
          </Button>
          <template v-if="deleteElement && ((editElementTitle && !isEditingTitle) || !editElementTitle)">
            <ConfirmDialog
              v-if="confirmDeletion"
              title="Delete Item"
              description="Are you sure you want to delete this? This action cannot be undone."
              confirm-label="Delete"
              @confirm="handleDelete">
              <template #trigger>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  class="size-6 hover:text-destructive"
                  aria-label="Delete"
                  data-cy="delete-element"
                  @click.stop>
                  <Trash2 class="size-3.5" />
                </Button>
              </template>
            </ConfirmDialog>
            <Button
              v-else
              variant="ghost"
              size="icon-sm"
              class="size-6 hover:text-destructive"
              aria-label="Delete"
              data-cy="delete-element"
              @click.stop="deleteElement(index)">
              <Trash2 class="size-3.5" />
            </Button>
          </template>
        </div>
        <div
          v-if="$slots.actions"
          class="flex items-center gap-0.5"
          @click.stop>
          <slot name="actions" />
        </div>
      </div>
    </div>
    <div
      v-if="$slots['additional-content']"
      class="flex items-center gap-1 px-1.5 pb-1.5"
      @click="selectElement(index)">
      <slot name="additional-content" />
    </div>
  </div>
</template>
